import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  afterNextRender,
  afterRenderEffect,
  computed,
  effect,
  inject,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { TextBoxComponent } from '@progress/kendo-angular-inputs';
import { SubSink } from 'subsink';
import { AuthService } from '../../../../auth/auth.service';
import { TodoEntryModel } from '../../../../core/models/todo-planner/todo-entry.model';
import { CalendarView } from '../../../../core/routing/mapping/page-navigation/time-tracking-navigation';
import { ShiveRenderService } from '../../../../core/services/rendering/shive-render.service';
import { DrawerService } from '../../../../core/services/ui/drawer.service';
import { TodoPlannerHttpService } from '../../../../core/state/todo-planner/todo-planner-http.service';
import { mapToArray } from '../../../../shared/functions/map-functions';
import { WINDOW } from '../../../calendar/util/browser-window-ref';
import { DateTimeHelper } from '../../../calendar/util/date-time-helper';
import { TodoContainerDirective } from '../../directives/todo-container.directive';
import { TodoEntryWrapperDirective } from '../../directives/todo-entry-wrapper.directive';
import { TodoPlannerService } from '../../services/todo-planner.service';
import { TodoContainerDropService } from '../../todo-entry/services/drag-and-drop/todo-container-drop.service';
import { TodoEntryComponentHandlerService } from '../../todo-entry/services/todo-entry-component-handler.service';
import { TodoEntryService } from '../../todo-entry/services/todo-entry.service';
import {
  AddToDoButtonWrapperCSSClass,
  RestrictionAreaCSSClass,
  TodoEntryFixIntervalCSSClass,
  TodoEntryFixIntervalWrapperCSSClass,
  TodoEntryVariableIntervalCSSClass,
  TodoEntryVariableIntervalWrapperCSSClass,
  TodoPlannerDropzoneCSSClass,
  TodoPlannerFixIntervalContainerGap,
} from '../../utils/todo-planner.constants';
import { TodoPlannerBaseView } from '../todo-planner-base-view';

@Component({
  selector: 'app-todo-planner-week-view',
  templateUrl: './todo-planner-week-view.component.html',
  styleUrl: './todo-planner-week-view.component.scss',
  standalone: false,
})
export class TodoPlannerWeekViewComponent extends TodoPlannerBaseView implements OnInit, OnDestroy {
  // Signals and view child / children
  fixIntervalLaneContainer = viewChild<ElementRef<HTMLDivElement>>('fixIntervalLaneContainer');

  fixIntervalHTMLWrapper = viewChildren('fixIntervalTodoWrapper', {
    read: TodoEntryWrapperDirective,
  });
  fixIntervalContainer = viewChildren('fixIntervalTodoContainer', { read: TodoContainerDirective });
  variableIntervalHTMLWrapper = viewChildren('variableIntervalTodoWrapper', {
    read: TodoEntryWrapperDirective,
  });
  variableIntervalContainer = viewChildren('variableIntervalTodoContainer', {
    read: TodoContainerDirective,
  });

  weekDays = computed(() => {
    return this.todoPlannerService.weekDays();
  });
  noFixedIntervalTodoEntries = signal(true);
  addTodoBox = viewChild<TextBoxComponent>('addTodoBox');

  // Constants
  public readonly DateTimeHelper = DateTimeHelper;
  public readonly CalendarView = CalendarView;
  public readonly faPlus = faPlus;
  public readonly CssDropzone = TodoPlannerDropzoneCSSClass;
  public readonly TodoEntryVariableIntervalWrapper = TodoEntryVariableIntervalWrapperCSSClass;
  public readonly TodoEntryFixIntervalWrapper = TodoEntryFixIntervalWrapperCSSClass;
  public readonly RestrictionAreaCSSClass = RestrictionAreaCSSClass;
  public readonly TodoEntryVariableInterval = TodoEntryVariableIntervalCSSClass;
  public readonly TodoEntryFixInterval = TodoEntryFixIntervalCSSClass;
  public readonly AddToDoButtonWrapperCSSClass = AddToDoButtonWrapperCSSClass;

  // DI
  public readonly todoPlannerService = inject(TodoPlannerService);
  public readonly todoEntryService = inject(TodoEntryService);
  public readonly todoEntryComponentHandlerService = inject(TodoEntryComponentHandlerService);
  private readonly todoContainerDropService = inject(TodoContainerDropService);
  private readonly todoPlannerEntriesHttpService = inject(TodoPlannerHttpService);
  private readonly drawerService = inject(DrawerService);
  private readonly authService = inject(AuthService);
  private readonly shiveRenderService = inject(ShiveRenderService);
  private readonly window = inject<Window>(WINDOW);

  // Misc
  private readonly subs = new SubSink();

  constructor() {
    super();

    effect(() => {
      // Init fix interval wrapper and container.
      this.todoPlannerService.todoPlannerModel.fixIntervalTodoWrapper = [
        ...this.fixIntervalHTMLWrapper(),
      ];
      this.todoPlannerService.todoPlannerModel.fixIntervalTodoContainer = [
        ...this.fixIntervalContainer(),
      ];

      // Init variable interval lanes.
      this.todoPlannerService.todoPlannerModel.variableIntervalTodoWrapper = [
        ...this.variableIntervalHTMLWrapper(),
      ];
      this.todoPlannerService.todoPlannerModel.variableIntervalTodoContainer = [
        ...this.variableIntervalContainer(),
      ];

      // if dropzones depend on the current DOM, re-init when queries change
      this.todoContainerDropService.initContainerDropzones();
    });

    afterNextRender(() => {
      // DOM for this component has been rendered for the next render pass
      this.setFixIntervalPlannerContainerWidth();

      this.subs.sink = this.todoPlannerService.parallelIndexChanged$.subscribe(
        (maxParallelIndex) => {
          if (maxParallelIndex < 0) {
            this.shiveRenderService.setStyle(
              this.fixIntervalLaneContainer().nativeElement,
              'height',
              '0',
            );
            this.noFixedIntervalTodoEntries.set(true);
            return;
          }

          this.noFixedIntervalTodoEntries.set(false);
          const rowHeight =
            this.calculateFixIntervalContainerHeight() * (maxParallelIndex + 1) +
            TodoPlannerFixIntervalContainerGap / 2;
          this.shiveRenderService.setStyle(
            this.fixIntervalLaneContainer().nativeElement,
            'height',
            `${rowHeight}px`,
          );
        },
      );

      if (this.window.innerWidth <= 768) {
        this.scrollToLane();
      }
    });

    this.setInputFocus();
  }

  ngOnInit(): void {
    this.subs.sink = this.drawerService.toggleDrawerContentWidth$.subscribe(() => {
      this.setFixIntervalPlannerContainerWidth();
      const fixInterTodoEntryMap =
        this.todoPlannerService.todoPlannerModel.fixIntervalTodoEntryModels;

      const fixIntervalTodoEntries = mapToArray(fixInterTodoEntryMap, true) as TodoEntryModel[];
      fixIntervalTodoEntries.forEach((todoEntryModel) => {
        const width =
          this.todoEntryComponentHandlerService.calculateTodoEntryFixIntervalWidth(todoEntryModel);
        const componentRef = todoEntryModel.componentRef;
        componentRef.setInput('todoEntryWidth', width);
      });
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public addTodo(day: Date, todoValue: string) {
    if (todoValue === '') {
      this.resetInput();
      return;
    }

    const user = this.authService.getLoggedInUserClone();
    const newTodoEntryModel = this.todoEntryService.createTodo(day, todoValue, user.id);
    this.todoPlannerEntriesHttpService.addTodoPlannerEntry(newTodoEntryModel);
    this.resetInput();
  }

  public resetInput(): void {
    this.todoValue = '';
    this.todoPlannerService.activeAddTodoEntryLaneId.set(-1);
  }

  public removeHoverState(addTodoBox: TextBoxComponent) {
    this.shiveRenderService.removeClass(addTodoBox.hostElement.nativeElement, 'k-hover');
  }

  private setFixIntervalPlannerContainerWidth() {
    const plannerModel = this.todoPlannerService.todoPlannerModel;
    const firstContainer = this.fixIntervalHTMLWrapper()[0].elementRef.nativeElement;
    plannerModel.containerWidth = firstContainer.getBoundingClientRect().width;
  }

  private calculateFixIntervalContainerHeight(): number {
    const plannerEventModelsMap =
      this.todoPlannerService.todoPlannerModel.fixIntervalTodoEntryModels;
    const todoEntries = mapToArray(plannerEventModelsMap, true) as TodoEntryModel[];

    if (todoEntries.length === 0) {
      return -1;
    }

    return todoEntries[0].componentRef.instance
      .todoEntryWrapper()
      .nativeElement.getBoundingClientRect().height;
  }

  private setInputFocus() {
    afterRenderEffect(() => {
      const laneId = this.todoPlannerService.activeAddTodoEntryLaneId();

      // Only focus when we are in "add mode"
      if (laneId < 0) {
        return;
      }

      const tb = this.addTodoBox();
      if (!tb) {
        return;
      }

      queueMicrotask(() => {
        tb.focus();
      });
    });
  }

  private scrollToLane(laneIndex?: number) {
    // If no laneIndex was set, scroll to today's lane
    let target: HTMLElement;
    if (laneIndex) {
      const lanes = document.querySelectorAll<HTMLElement>('.variable-interval .lane');
      target = lanes[laneIndex];
    } else {
      target = document.querySelector<HTMLElement>('.variable-interval .lane.today');
    }

    if (!target) {
      return;
    }

    target.scrollIntoView({
      behavior: 'smooth',
      inline: 'start',
      block: 'nearest',
    });
  }
}
